import { toast } from '@/hooks/use-toast';

export interface RetryConfig {
  maxRetries: number;
  baseDelay: number;
  maxDelay: number;
  backoffMultiplier: number;
}

export const DEFAULT_RETRY_CONFIG: RetryConfig = {
  maxRetries: 3,
  baseDelay: 1000,
  maxDelay: 10000,
  backoffMultiplier: 2,
};

export class NetworkError extends Error {
  constructor(
    message: string,
    public readonly status?: number,
    public readonly code?: string
  ) {
    super(message);
    this.name = 'NetworkError';
  }
}

export const isNetworkError = (error: unknown): boolean => {
  if (error instanceof NetworkError) return true;
  if (error instanceof Error) {
    return (
      error.message.includes('Failed to fetch') ||
      error.message.includes('Network request failed') ||
      error.message.includes('Connection error') ||
      error.message.includes('TypeError: Failed to fetch')
    );
  }
  return false;
};

export const shouldRetry = (error: unknown, attempt: number, maxRetries: number): boolean => {
  if (attempt >= maxRetries) return false;
  return isNetworkError(error);
};

export const calculateDelay = (attempt: number, config: RetryConfig): number => {
  const delay = config.baseDelay * Math.pow(config.backoffMultiplier, attempt);
  return Math.min(delay + Math.random() * 1000, config.maxDelay); // Add jitter
};

export const sleep = (ms: number): Promise<void> => {
  return new Promise(resolve => setTimeout(resolve, ms));
};

export async function withRetry<T>(
  operation: () => Promise<T>,
  config: Partial<RetryConfig> = {},
  onRetry?: (attempt: number, error: unknown) => void
): Promise<T> {
  const finalConfig = { ...DEFAULT_RETRY_CONFIG, ...config };
  let lastError: unknown;

  for (let attempt = 0; attempt <= finalConfig.maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error;

      if (!shouldRetry(error, attempt, finalConfig.maxRetries)) {
        throw error;
      }

      if (attempt < finalConfig.maxRetries) {
        const delay = calculateDelay(attempt, finalConfig);
        onRetry?.(attempt + 1, error);
        await sleep(delay);
      }
    }
  }

  throw lastError;
}

export const handleNetworkError = (error: unknown, context: string): void => {
  console.error(`Network error in ${context}:`, error);

  if (isNetworkError(error)) {
    toast({
      title: 'Connection Error',
      description: 'Unable to connect to the server. Please check your internet connection and try again.',
      variant: 'destructive',
    });
  } else {
    toast({
      title: 'Error',
      description: `Failed to ${context}. Please try again.`,
      variant: 'destructive',
    });
  }
};

// ==========================================
// LRU Cache Implementation
// ==========================================

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number;
}

/**
 * A bounded LRU (Least Recently Used) cache with TTL support
 */
class LRUCache<T> {
  private cache: Map<string, CacheEntry<T>> = new Map();
  private readonly maxSize: number;

  constructor(maxSize: number = 100) {
    this.maxSize = maxSize;
  }

  get(key: string): T | null {
    const entry = this.cache.get(key);

    if (!entry) {
      return null;
    }

    // Check if expired
    if (Date.now() - entry.timestamp >= entry.ttl) {
      this.cache.delete(key);
      return null;
    }

    // Move to end (most recently used)
    this.cache.delete(key);
    this.cache.set(key, entry);

    return entry.data;
  }

  set(key: string, data: T, ttl: number): void {
    // Remove existing entry if present
    if (this.cache.has(key)) {
      this.cache.delete(key);
    }

    // Evict oldest entries if at capacity
    while (this.cache.size >= this.maxSize) {
      const oldestKey = this.cache.keys().next().value;
      if (oldestKey) {
        this.cache.delete(oldestKey);
      }
    }

    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl,
    });
  }

  delete(key: string): boolean {
    return this.cache.delete(key);
  }

  has(key: string): boolean {
    const entry = this.cache.get(key);
    if (!entry) return false;

    // Check if expired
    if (Date.now() - entry.timestamp >= entry.ttl) {
      this.cache.delete(key);
      return false;
    }

    return true;
  }

  clear(): void {
    this.cache.clear();
  }

  get size(): number {
    return this.cache.size;
  }

  /**
   * Removes all expired entries
   */
  cleanup(): void {
    const now = Date.now();
    for (const [key, entry] of this.cache.entries()) {
      if (now - entry.timestamp >= entry.ttl) {
        this.cache.delete(key);
      }
    }
  }
}

// Global caches with bounded size
const requestCache = new Map<string, Promise<unknown>>();
const dataCache = new LRUCache<unknown>(100); // Max 100 cached items

// Periodic cleanup of expired entries (every 5 minutes)
if (typeof window !== 'undefined') {
  setInterval(() => {
    dataCache.cleanup();
  }, 5 * 60 * 1000);
}

export const getCachedData = <T>(key: string): T | null => {
  return dataCache.get(key) as T | null;
};

export const setCachedData = <T>(key: string, data: T, ttl: number = 30000): void => {
  dataCache.set(key, data, ttl);
};

export const clearCachedData = (key: string): void => {
  dataCache.delete(key);
};

export const createDeduplicatedRequest = <T>(
  key: string,
  operation: () => Promise<T>,
  ttl: number = 30000
): Promise<T> => {
  // Check cache first
  const cached = getCachedData<T>(key);
  if (cached) {
    return Promise.resolve(cached);
  }

  // Check if request is already in flight
  if (requestCache.has(key)) {
    return requestCache.get(key) as Promise<T>;
  }

  // Create new request
  const promise = operation().then(result => {
    setCachedData(key, result, ttl);
    requestCache.delete(key);
    return result;
  }).catch(error => {
    requestCache.delete(key);
    throw error;
  });

  requestCache.set(key, promise);

  // Cleanup request cache if it gets too large
  if (requestCache.size > 50) {
    const oldestKey = requestCache.keys().next().value;
    if (oldestKey) {
      requestCache.delete(oldestKey);
    }
  }

  return promise;
};

export const createNetworkAwareOperation = <T>(
  operation: () => Promise<T>,
  context: string,
  config?: Partial<RetryConfig>
): Promise<T> => {
  return withRetry(
    operation,
    {
      maxRetries: 2,
      baseDelay: 2000,
      maxDelay: 8000,
      ...config
    },
    (attempt, error) => {
      console.log(`Retrying ${context} (attempt ${attempt}):`, error);
      if (attempt === 1) {
        toast({
          title: 'Retrying...',
          description: `Connection failed, retrying ${context}...`,
        });
      }
    }
  ).catch((error) => {
    handleNetworkError(error, context);
    throw error;
  });
};

/**
 * Invalidate all cached data (useful after logout)
 */
export const invalidateAllCache = (): void => {
  dataCache.clear();
  requestCache.clear();
};
